import Vue from 'vue'
import Vuex from 'vuex'
import {
	ethers,
	BrowserProvider,
	Contract,
	BigNumber
} from "ethers";
import Token from '@/handlers/token';
import Pool from '@/handlers/pool.js';
import $envConfig from '@/common/config.js';
import {
	erc20Abi,
	vantTokenAbi
} from '@/contracts';

import {
	formatNumber,
	getRandomInt
} from "@/utils/index.js"

Vue.use(Vuex)


// 标记需要永久存储的变量，在每次启动时取出，在state中的变量名
let saveStateKeys = ['vuex_user']

// 保存变量到本地存储
const saveLifeData = function (key, value) {
	// 判断变量是否在存储数组中
	if (saveStateKeys.indexOf(key) != -1) {
		// 获取本地存储的lifeData对象，将变量添加到对象中
		let tmpLifeData = window.localStorage.getItem('lifeData')
		// 第一次启动时不存在，则放一个空对象
		tmpLifeData = tmpLifeData ? tmpLifeData : {},
			tmpLifeData[key] = value
		// 将变量再次放回本地存储中
		window.localStorage.setItem('lifeData', tmpLifeData)
	}
}


const store = new Vuex.Store({
	state: {
		walletAddress: null, // 当前连接的钱包地址
		chainId: null, // 当前区块链网络ID
		isConnected: false, // 连接状态标识
		provider: null, // ethers.js的Provider实例
		signer: null,
		appTokenInstance: null,
		poolInstance: null,
		parentAddress: -1, //邀请人地址
		lastMintTime: null,
		user: null,
		isChainCorrect: false,
		points: 0,
		isRegistered: false,
		coolDown: 0,
		costFee: 0,
		ownerAddress: '',
	},
	mutations: {
		SET_WALLET(state, {
			address,
			chainId
		}) {
			state.walletAddress = address
			state.chainId = chainId
			state.isConnected = !!address
			state.isChainCorrect = $envConfig.DEFAULT_CHAINID != chainId;
		},
		SET_PROVIDER(state, provider) {
			state.provider = provider
		},
		SET_SIGNER(state, signer) {
			state.signer = signer
		},
		SET_APP_TOKEN_INSTANCE(state, instance) {
			state.appTokenInstance = instance;
		},
		SET_POOL_INSTANCE(state, instance) {
			state.poolInstance = instance;
		},
		SET_PARENT_ADDRESS(state, address) {
			state.parentAddress = address
		},
		SET_LASTMINTTIME(state, timestamp) {
			state.lastMintTime = timestamp;
		},
		SET_USERINFO(state, user) {
			state.user = user;
		},
		SET_IS_REGISTER(state, isRegistered) {
			state.isRegistered = isRegistered;
		},
		SET_COOLDOWN(state, coolDown) {
			state.coolDown = coolDown;
		},
		SET_COSTFEE(state, costFee) {
			state.costFee = costFee;
		},
		SET_OWNER(state, ownerAddress) {
			state.ownerAddress = ownerAddress;
		}
	},
	actions: {
		$tStore(state, payload) {
			// 判断是否多层调用，state中为对象存在的情况，例如user.info.score = 1
			let nameArr = payload.name.split('.')
			let saveKey = ''
			let len = nameArr.length
			if (len >= 2) {
				let obj = state[nameArr[0]]
				for (let i = 1; i < len - 1; i++) {
					obj = obj[nameArr[i]]
				}
				obj[nameArr[len - 1]] = payload.value
				saveKey = nameArr[0]
			} else {
				// 单层级变量
				state[payload.name] = payload.value
				saveKey = payload.name
			}

			// 保存变量到本地中
			saveLifeData(saveKey, state[saveKey])
		},
		async connectWallet({
			commit
		}) {
			if (window.ethereum) {
				try {
					const provider = new BrowserProvider(window.ethereum);

					const accounts = await provider.send('eth_requestAccounts', []);
					const signer = await provider.getSigner();
					const address = await signer.getAddress();
					const network = await provider.getNetwork();


					commit('SET_PROVIDER', provider);
					commit('SET_SIGNER', signer);

					commit('SET_WALLET', {
						address: address,
						chainId: network.chainId
					})

					localStorage.setItem('walletState', JSON.stringify({
						address: address.toString(),
						chainId: network.chainId.toString()
					}))


					if (!store.state.isChainCorrect) {
						await store.dispatch("getPoolInstance")
					}
				} catch (error) {
					console.error('連接失敗:', error)
				}
			} else {
				console.log("請安裝MetaMask擴展插件")
			}
		},
		// 断开连接
		disconnectWallet({
			commit
		}) {
			commit('SET_WALLET', {
				address: null,
				chainId: null
			})
			localStorage.removeItem('walletState')
		},
		async getPoolInstance({
			commit
		}) {
			try {
				let instance = new Pool($envConfig.VUE_MAIN_CONTRACT, store.state.provider, store.state
					.signer, $envConfig.VUE_APP_TOKEN);
				commit("SET_POOL_INSTANCE", instance);
				store.dispatch("getUserInfo");
				store.dispatch("getIsRegistered");
				store.dispatch("getCoolDown");
				store.dispatch("getOwnerAddress");
			} catch (err) {
				console.log(err);
			}
		},
		async getUserInfo({
			commit
		}) {
			Vue.prototype.$withRetry(
				() => store.state.poolInstance.getUsers(store.state.walletAddress),
				{ retries: 3 }
			).then((user) => {
				if (user) {
					// console.log(user)
					commit("SET_USERINFO", {
						uplines: user[0],
						lastCheckIn: user[1].toString(),
						tokenRewardCount: user[2].toString(),
						pointCount: user[3].toString(),
						totalPointsClaimed: user[4].toString(),
						checkInTotalCount: user[5].toString(),
						claimedExtraTiers: user[6].toString(),
						pendingTokenRewards: user[7].toString()
					})
				}
			}).catch(error => {
				if (error.name === 'MaxRetriesError') {
					console.log(`getUserInfo失败，已重试${error.attempt}次`);
				}
			});
		},
		async getIsRegistered({
			commit
		}) {
			Vue.prototype.$withRetry(
				() => store.state.poolInstance.isRegistered(store.state.walletAddress),
				{ retries: 3 }
			).then((isRegistered) => {
				console.log("isRegistered: ", isRegistered ? true : false)
				commit("SET_IS_REGISTER", isRegistered ? true : false)
			}).catch(error => {
				if (error.name === 'MaxRetriesError') {
					console.log(`getUserInfo失败，已重试${error.attempt}次`);
				}
			});
		},
		async getTokenInstance({
			commit
		}) {
			let instance = new Token($envConfig.VUE_APP_TOKEN, store.state.signer);
			commit("SET_APP_TOKEN_INSTANCE", instance);
		},
		async getCoolDown({
			commit
		}) {
			Vue.prototype.$withRetry(
				() => store.state.poolInstance.getCoolDown(),
				{ retries: 3 }
			).then((cooldown) => {
				commit("SET_COOLDOWN", cooldown);
			}).catch(error => {
				if (error.name === 'MaxRetriesError') {
					console.log(`操作失败，已重试${error.attempt}次`);
				}
			});
		},
		async getOwnerAddress({
			commit
		}) {
			let owner = await store.state.poolInstance.getOwner();
			commit("SET_OWNER", owner);
		},
		async changeChain() {
			if (window.ethereum) {
				try {
					await window.ethereum.request({
						method: "wallet_switchEthereumChain",
						params: [{
							chainId: $envConfig.DEFAULT_CHAINID
						}],
					});
					return true;
				} catch (e) {
					const err = e;
					console.log(err);
					if (err.code === 4902) {
						try {
							await window.ethereum.request({
								method: "wallet_addEthereumChain",
								params: [$envConfig.chainConfig[$envConfig.DEFAULT_CHAINID]],
							});
						} catch (addError) {
							console.error(addError);
						}
					}
					return true;
				}
			} else {
				console.log("請等待初始化完成")
			}
		}
	}
})



window.addEventListener("load", async () => {
	// Modern dapp browsers...
	if (window.ethereum) {
		try {
			await window.ethereum.enable();
			// Acccounts now exposed
			store.dispatch("connectWallet");

		} catch (error) {
			console.log(error)
			// User denied account access...
		}

		window.ethereum.on('accountsChanged', (accounts) => {
			// Handle the new accounts, or lack thereof.
			// "accounts" will always be an array, but it can be empty.
			// store.dispatch("connectWallet");
			window.location.reload();
		});

		window.ethereum.on('chainChanged', (chainId) => {
			// Handle the new chain.
			// Correctly handling chain changes can be complicated.
			// We recommend reloading the page unless you have good reason not to.
			window.location.reload();
		});

	}
	// Legacy dapp browsers...
	else if (window.web3) {
		// Acccounts always exposed
	}
	// Non-dapp browsers...
	else {
		console.log(
			"Non-Ethereum browser detected. You should consider trying MetaMask!"
		);
		return;
	}
});

export default store